'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  FileText,
  Package,
  Mail,
  MessageSquare,
  TrendingUp,
  Users,
  Clock,
  ArrowRight,
  Wallet,
  UserCheck,
  MessageCircle,
  AlertCircle
} from 'lucide-react';
import ExchangeRateWidget from './_components/ExchangeRateWidget';

interface DashboardPermissions {
  hasCmsArticle: boolean;
  hasCmsProduct: boolean;
  hasCmsInquiry: boolean;
  hasAiCustomer: boolean;
  hasSystemConfig: boolean;
  hasCrmAccess: boolean;
  canViewAllCrm: boolean;
}

interface DashboardStats {
  permissions: DashboardPermissions;
  crm?: {
    totalContacts: number;
    followingContacts: number;
    unrepliedMessages: number;
    pendingFollowUps: number;
  };
  inquiries?: {
    total: number;
    pending: number;
    assigned: number;
  };
  articles?: {
    total: number;
  };
  aiChats?: {
    total: number;
    today: number;
  };
  balance?: {
    cashBalance: string;
    availableBalance: string;
  };
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoadError(null);
      const response = await fetch('/api/admin/dashboard', { credentials: 'include' });
      const data = await response.json();

      if (response.ok && data.success) {
        setStats(data.stats);
        setCurrentUser(data.user);
        // 同步到 sessionStorage
        if (data.user) {
          sessionStorage.setItem('adminUser', JSON.stringify(data.user));
        }
      } else {
        setLoadError(data.error || '加载数据失败');
        console.error('Dashboard API error:', data);
      }
    } catch (error) {
      console.error('加载统计数据失败:', error);
      setLoadError('网络错误，请刷新页面重试');
    } finally {
      setLoadingStats(false);
    }
  };

  const permissions = stats?.permissions;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '上午好';
    if (hour < 18) return '下午好';
    return '晚上好';
  };

  // 根据权限过滤快捷操作
  const quickActions = [
    permissions?.hasCmsArticle && { label: '博客管理', href: '/admin-2025/blog', icon: FileText, color: 'blue' },
    permissions?.hasCmsProduct && { label: '产品管理', href: '/admin-2025/products', icon: Package, color: 'purple' },
    permissions?.hasCmsInquiry && { label: '询盘管理', href: '/admin-2025/inquiries', icon: Mail, color: 'green' },
    permissions?.hasAiCustomer && { label: 'AI客服', href: '/admin-2025/ai-customer-service', icon: MessageSquare, color: 'orange' },
    permissions?.hasCrmAccess && { label: 'CRM客户', href: '/admin-2025/crm/contacts', icon: Users, color: 'cyan' },
  ].filter(Boolean) as { label: string; href: string; icon: any; color: string }[];

  return (
    <div className="space-y-6">
      {/* 欢迎区域 */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {getGreeting()}，{currentUser?.username || '管理员'}
        </h1>
        <p className="text-gray-500 mt-1">
          {new Date().toLocaleDateString('zh-CN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long'
          })}
        </p>
      </div>

      {/* 汇率显示 */}
      <ExchangeRateWidget />

      {/* 加载状态 */}
      {loadingStats && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <span className="ml-3 text-gray-500">加载中...</span>
          </div>
        </div>
      )}

      {/* 错误提示 */}
      {loadError && (
        <div className="bg-red-50 rounded-lg border border-red-200 p-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-600">{loadError}</span>
          </div>
        </div>
      )}

      {/* CRM 统计卡片 - 仅有权限时显示 */}
      {permissions?.hasCrmAccess && stats?.crm && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link href="/admin-2025/crm/contacts" className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  {permissions.canViewAllCrm ? '总客户数' : '我的客户'}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {loadingStats ? '-' : stats.crm.totalContacts}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center">
                <Users className="w-5 h-5 text-cyan-600" />
              </div>
            </div>
          </Link>

          <Link href="/admin-2025/crm/contacts?filter=following" className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">正在跟进</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {loadingStats ? '-' : stats.crm.followingContacts}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </Link>

          <Link href="/admin-2025/communications/inbox" className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">待回复消息</p>
                <p className={`text-2xl font-bold mt-1 ${stats.crm.unrepliedMessages > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                  {loadingStats ? '-' : stats.crm.unrepliedMessages}
                </p>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stats.crm.unrepliedMessages > 0 ? 'bg-orange-50' : 'bg-gray-50'}`}>
                <MessageCircle className={`w-5 h-5 ${stats.crm.unrepliedMessages > 0 ? 'text-orange-600' : 'text-gray-400'}`} />
              </div>
            </div>
          </Link>

          <Link href="/admin-2025/crm/contacts?filter=pending" className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">待跟进客户</p>
                <p className={`text-2xl font-bold mt-1 ${stats.crm.pendingFollowUps > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {loadingStats ? '-' : stats.crm.pendingFollowUps}
                </p>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stats.crm.pendingFollowUps > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                <AlertCircle className={`w-5 h-5 ${stats.crm.pendingFollowUps > 0 ? 'text-red-600' : 'text-gray-400'}`} />
              </div>
            </div>
          </Link>
        </div>
      )}

      {/* 其他统计卡片 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 文章统计 - 仅有权限时显示 */}
        {permissions?.hasCmsArticle && (
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">文章总数</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {loadingStats ? '-' : stats?.articles?.total || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
        )}

        {/* 询盘统计 - 仅有权限时显示 */}
        {permissions?.hasCmsInquiry && stats?.inquiries && (
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">待处理询盘</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {loadingStats ? '-' : stats.inquiries.pending}
                </p>
                {stats.inquiries.assigned > 0 && !permissions.canViewAllCrm && (
                  <p className="text-xs text-gray-400 mt-1">
                    我负责: {stats.inquiries.assigned}
                  </p>
                )}
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                <Mail className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>
        )}

        {/* AI客服统计 - 仅有权限时显示 */}
        {permissions?.hasAiCustomer && stats?.aiChats && (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">今日AI对话</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {loadingStats ? '-' : stats.aiChats.today}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-orange-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">AI对话总数</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {loadingStats ? '-' : stats.aiChats.total}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </div>
          </>
        )}

        {/* 系统余额 - 仅有权限时显示 */}
        {permissions?.hasSystemConfig && stats?.balance && (
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">火山引擎余额</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  ¥{stats.balance.cashBalance}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  可用: ¥{stats.balance.availableBalance}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-yellow-50 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 快捷操作 */}
      {quickActions.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">快捷操作</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
            {quickActions.map((action) => (
              <Link
                key={action.href}
                href={action.href}
                className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all group"
              >
                <div className={`
                  w-12 h-12 rounded-lg flex items-center justify-center mb-3
                  ${action.color === 'blue' ? 'bg-blue-50 text-blue-600' : ''}
                  ${action.color === 'purple' ? 'bg-purple-50 text-purple-600' : ''}
                  ${action.color === 'green' ? 'bg-green-50 text-green-600' : ''}
                  ${action.color === 'orange' ? 'bg-orange-50 text-orange-600' : ''}
                  ${action.color === 'cyan' ? 'bg-cyan-50 text-cyan-600' : ''}
                `}>
                  <action.icon className="w-6 h-6" />
                </div>
                <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                  {action.label}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* 系统状态 */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">系统状态</h2>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span className="text-gray-600">系统运行正常</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">
              最后登录时间：{new Date().toLocaleString('zh-CN')}
            </span>
          </div>
          {!permissions?.canViewAllCrm && permissions?.hasCrmAccess && (
            <div className="flex items-center gap-3 text-sm">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">
                您正在查看自己负责的客户数据
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
